import React, { useState, useEffect, useRef } from 'react';
import { DndContext, useDraggable, DragEndEvent, PointerSensor, TouchSensor, KeyboardSensor, useSensor, useSensors } from '@dnd-kit/core';
import { logger } from '../utils/logger';

interface Position {
  x: number;
  y: number;
}

interface DraggablePanelProps {
  children: React.ReactNode;
  initialPosition?: Position;
  storageKey?: string;
  isVisible?: boolean;
  className?: string;
  onPositionChange?: (position: Position) => void;
}

const STORAGE_PREFIX = 'draggable-panel-';

export const DraggablePanel: React.FC<DraggablePanelProps> = ({
  children,
  initialPosition = { x: 24, y: 24 },
  storageKey = 'my-trips',
  isVisible = true,
  className = '',
  onPositionChange
}) => {
  const [position, setPosition] = useState<Position>(initialPosition);
  const [isDragging, setIsDragging] = useState(false);
  const panelRef = useRef<HTMLDivElement>(null);

  // Load position from localStorage on mount
  useEffect(() => {
    const savedPosition = localStorage.getItem(`${STORAGE_PREFIX}${storageKey}`);
    if (savedPosition) {
      try {
        const parsed = JSON.parse(savedPosition);
        setPosition(parsed);
      } catch (error) {
        logger.warn('Failed to parse saved panel position:', error);
      }
    }
  }, [storageKey]);

  // Save position to localStorage when it changes
  useEffect(() => {
    localStorage.setItem(`${STORAGE_PREFIX}${storageKey}`, JSON.stringify(position));
    onPositionChange?.(position);
  }, [position, storageKey, onPositionChange]);

  // Constrain position within viewport bounds
  const constrainPosition = (newPosition: Position): Position => {
    if (!panelRef.current) return newPosition;

    const panel = panelRef.current;
    const rect = panel.getBoundingClientRect();
    const viewport = {
      width: window.innerWidth,
      height: window.innerHeight
    };

    const minX = 0;
    const maxX = Math.max(0, viewport.width - rect.width);
    
    // For Y-axis: Allow panel to be positioned even when taller than viewport
    // Keep at least 100px of the top (drag handle area) visible
    const dragHandleHeight = 100;
    const minY = Math.min(0, viewport.height - rect.height);
    const maxY = viewport.height - dragHandleHeight;

    return {
      x: Math.max(minX, Math.min(maxX, newPosition.x)),
      y: Math.max(minY, Math.min(maxY, newPosition.y))
    };
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { delta } = event;
    
    if (delta) {
      const newPosition = constrainPosition({
        x: position.x + delta.x,
        y: position.y + delta.y
      });
      setPosition(newPosition);
    }
    
    setIsDragging(false);
  };

  // Configure sensors for smooth dragging - must be before any returns
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(TouchSensor, {
      activationConstraint: {
        delay: 250,
        tolerance: 5,
      },
    }),
    useSensor(KeyboardSensor)
  );

  const resetPosition = () => {
    setPosition(initialPosition);
  };

  if (!isVisible) return null;

  return (
    <DndContext sensors={sensors} onDragEnd={handleDragEnd}>
      <DraggableContent
        position={position}
        isDragging={isDragging}
        onDragStart={() => setIsDragging(true)}
        onResetPosition={resetPosition}
        className={className}
        ref={panelRef}
      >
        {children}
      </DraggableContent>
    </DndContext>
  );
};

interface DraggableContentProps {
  children: React.ReactNode;
  position: Position;
  isDragging: boolean;
  onDragStart: () => void;
  onResetPosition: () => void;
  className: string;
}


const DraggableContent = React.forwardRef<HTMLDivElement, DraggableContentProps>(
  ({ children, position, isDragging, onDragStart, onResetPosition, className }, ref) => {
    const {
      attributes,
      listeners,
      setNodeRef,
      transform,
    } = useDraggable({
      id: 'draggable-panel',
    });

    // Combine transform from dragging with fixed position
    const style: React.CSSProperties = {
      position: 'fixed',
      left: position.x,
      top: position.y,
      transform: transform ? `translate3d(${transform.x}px, ${transform.y}px, 0)` : undefined,
      zIndex: 30,
      // Removed transition to prevent snapping
      cursor: isDragging ? 'grabbing' : 'auto',
      filter: isDragging ? 'drop-shadow(0 20px 25px rgb(0 0 0 / 0.15))' : 'drop-shadow(0 10px 15px rgb(0 0 0 / 0.1))',
    };

    // Pass drag attributes and handlers to children
    const dragProps = {
      dragAttributes: attributes,
      dragListeners: listeners,
      isDragging,
      onResetPosition,
      onDragStart: () => {
        onDragStart();
      }
    };

    return (
      <div
        ref={(node) => {
          setNodeRef(node);
          if (ref) {
            if (typeof ref === 'function') {
              ref(node);
            } else {
              ref.current = node;
            }
          }
        }}
        style={style}
        className={`${className} ${isDragging ? 'select-none' : ''}`}
      >
        {React.isValidElement(children) 
          ? React.cloneElement(children, dragProps)
          : children
        }
      </div>
    );
  }
);

DraggableContent.displayName = 'DraggableContent';